import type React from 'react';
import {
  createContext,
  type Dispatch,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import type { FlagKeys } from './featureFlagProvider';

const STORAGE_KEY = '__nexus_flag_overrides__';

type FlagOverrides = Partial<FlagKeys>;

interface FeatureFlagOverrideContextType {
  overrides: FlagOverrides;
  setFlagOverride: (flagName: keyof FlagKeys, value: boolean) => void;
  clearFlagOverride: (flagName: keyof FlagKeys) => void;
  clearAllOverrides: () => void;
  hasOverrides: () => boolean;
  isDialogOpen: boolean;
  setIsDialogOpen: Dispatch<SetStateAction<boolean>>;
}

export const FeatureFlagOverrideContext = createContext<FeatureFlagOverrideContextType | undefined>(
  undefined,
);

/**
 * Load overrides from localStorage
 */
const loadOverrides = (): FlagOverrides => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as FlagOverrides;
    }
  } catch (error) {
    //biome-ignore lint/suspicious/noConsole: need to log this error
    console.error('Failed to load feature flag overrides from localStorage:', error);
  }
  return {};
};

/**
 * Save overrides to localStorage
 */
const saveOverrides = (overrides: FlagOverrides): void => {
  try {
    if (Object.keys(overrides).length === 0) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
    }
  } catch (error) {
    //biome-ignore lint/suspicious/noConsole: need to log this error
    console.error('Failed to save feature flag overrides to localStorage:', error);
  }
};

export const FeatureFlagOverrideProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [overrides, setOverrides] = useState<FlagOverrides>(loadOverrides);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Sync to localStorage whenever overrides change
  useEffect(() => {
    saveOverrides(overrides);
  }, [overrides]);

  const setFlagOverride = useCallback((flagName: keyof FlagKeys, value: boolean) => {
    setOverrides((prev) => ({
      ...prev,
      [flagName]: value,
    }));
  }, []);

  const clearFlagOverride = useCallback((flagName: keyof FlagKeys) => {
    setOverrides((prev) => {
      const newOverrides = { ...prev };
      delete newOverrides[flagName];
      return newOverrides;
    });
  }, []);

  const clearAllOverrides = useCallback(() => {
    setOverrides({});
  }, []);

  const hasOverrides = useCallback(() => {
    return Object.keys(overrides).length > 0;
  }, [overrides]);

  return (
    <FeatureFlagOverrideContext.Provider
      value={{
        overrides,
        setFlagOverride,
        clearFlagOverride,
        clearAllOverrides,
        hasOverrides,
        isDialogOpen,
        setIsDialogOpen,
      }}
    >
      {children}
    </FeatureFlagOverrideContext.Provider>
  );
};

/**
 * Hook to access feature flag override context
 */
export const useFeatureFlagOverrides = (): FeatureFlagOverrideContextType => {
  const context = useContext(FeatureFlagOverrideContext);
  if (!context) {
    throw new Error('useFeatureFlagOverrides must be used within FeatureFlagOverrideProvider');
  }
  return context;
};
